import { useState } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Spin,
  Tag,
  Statistic,
  Space,
  Empty,
} from 'antd';
import { LeftOutlined, RightOutlined, CalendarOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { tradesApi } from '@tradermind/shared/api';
import type { CalendarDay, CalendarWeek, CalendarEvaluation } from '@tradermind/shared/types';

const { Title, Text } = Typography;

function getRColor(r: number): string {
  if (r > 0) return '#10B981';
  if (r < 0) return '#EF4444';
  return '#9CA3AF';
}

function getRBgColor(r: number): string {
  if (r > 0) return '#F0FDF4';
  if (r < 0) return '#FEF2F2';
  return '#F9FAFB';
}

export default function ProfitCalendarPage() {
  const { t } = useTranslation('trade');
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const { data, isLoading } = useQuery({
    queryKey: ['profit-calendar', year, month],
    queryFn: () => tradesApi.getProfitCalendar({ year, month }),
  });

  const calendar = data?.data;

  const handlePrevMonth = () => {
    if (month === 1) {
      setYear(year - 1);
      setMonth(12);
    } else {
      setMonth(month - 1);
    }
  };

  const handleNextMonth = () => {
    if (month === 12) {
      setYear(year + 1);
      setMonth(1);
    } else {
      setMonth(month + 1);
    }
  };

  const handleToday = () => {
    setYear(now.getFullYear());
    setMonth(now.getMonth() + 1);
  };

  const monthLabel = new Date(year, month - 1).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0 }}>
          <CalendarOutlined style={{ marginRight: 8 }} />
          {t('profit_calendar.title')}
        </Title>
        <Space>
          <Button icon={<LeftOutlined />} onClick={handlePrevMonth} />
          <Button onClick={handleToday}>{monthLabel}</Button>
          <Button icon={<RightOutlined />} onClick={handleNextMonth} />
        </Space>
      </div>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 100 }}>
          <Spin size="large" />
        </div>
      ) : !calendar ? (
        <Empty description={t('profit_calendar.no_data')} />
      ) : (
        <Row gutter={[24, 24]}>
          {/* Calendar Grid */}
          <Col xs={24} lg={16}>
            <Card>
              <CalendarGrid days={calendar.days} weeks={calendar.weeks} t={t} />
            </Card>
          </Col>

          {/* Evaluation Panel */}
          <Col xs={24} lg={8}>
            <EvaluationPanel evaluation={calendar.evaluation} t={t} />
          </Col>
        </Row>
      )}
    </div>
  );
}

function CalendarGrid({
  days,
  weeks,
  t,
}: {
  days: CalendarDay[];
  weeks: CalendarWeek[];
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  // Build grid rows: each row is a week (Mon-Sun + weekly total)
  const rows: Array<{ cells: (CalendarDay | null)[]; week: CalendarWeek | null }> = [];
  let currentRow: (CalendarDay | null)[] = new Array(7).fill(null);
  let weekIdx = 0;

  for (const day of days) {
    // weekday: 0=Sun, remap: Mon=0...Sun=6 for display
    const col = day.weekday === 0 ? 6 : day.weekday - 1;
    currentRow[col] = day;

    // End of week (Sunday) or last day of month
    if (day.weekday === 0 || day === days[days.length - 1]) {
      rows.push({
        cells: currentRow,
        week: weeks[weekIdx] || null,
      });
      weekIdx++;
      currentRow = new Array(7).fill(null);
    }
  }

  const headerLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <div>
      {/* Header */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr) 80px',
          gap: 4,
          marginBottom: 8,
        }}
      >
        {headerLabels.map((label) => (
          <div key={label} style={{ textAlign: 'center', fontWeight: 600, fontSize: 12, color: '#6B7280', padding: '4px 0' }}>
            {label}
          </div>
        ))}
        <div style={{ textAlign: 'center', fontWeight: 600, fontSize: 12, color: '#6B7280', padding: '4px 0' }}>
          {t('profit_calendar.total')}
        </div>
      </div>

      {/* Rows */}
      {rows.map((row, rowIdx) => (
        <div
          key={rowIdx}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr) 80px',
            gap: 4,
            marginBottom: 4,
          }}
        >
          {row.cells.map((cell, cellIdx) => (
            <DayCell key={cellIdx} day={cell} />
          ))}
          <WeekTotalCell week={row.week} t={t} />
        </div>
      ))}
    </div>
  );
}

function DayCell({ day }: { day: CalendarDay | null }) {
  if (!day) {
    return <div style={{ minHeight: 60, background: '#F9FAFB', borderRadius: 6 }} />;
  }

  const dayNum = parseInt(day.date.split('-')[2], 10);
  const hasR = day.trade_count > 0;

  return (
    <div
      style={{
        minHeight: 60,
        background: hasR ? getRBgColor(day.r_total) : '#FFFFFF',
        borderRadius: 6,
        border: '1px solid #E5E7EB',
        padding: '4px 6px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <div style={{ fontSize: 11, color: '#9CA3AF' }}>{dayNum}</div>
      {hasR && (
        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              fontSize: 16,
              fontWeight: 700,
              color: getRColor(day.r_total),
            }}
          >
            {day.r_total > 0 ? '+' : ''}
            {day.r_total}R
          </div>
          <div style={{ fontSize: 10, color: '#9CA3AF' }}>
            {day.trade_count}
          </div>
        </div>
      )}
      {!hasR && day.has_trades && (
        <div style={{ textAlign: 'center', fontSize: 11, color: '#9CA3AF' }}>0R</div>
      )}
    </div>
  );
}

function WeekTotalCell({
  week,
  t,
}: {
  week: CalendarWeek | null;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  if (!week || week.trade_count === 0) {
    return (
      <div
        style={{
          minHeight: 60,
          background: '#F9FAFB',
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Text type="secondary" style={{ fontSize: 12 }}>-</Text>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: 60,
        background: getRBgColor(week.r_total),
        borderRadius: 6,
        border: '1px solid #E5E7EB',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 4,
      }}
    >
      <div
        style={{
          fontSize: 14,
          fontWeight: 700,
          color: getRColor(week.r_total),
        }}
      >
        {week.r_total > 0 ? '+' : ''}
        {week.r_total}R
      </div>
      <div style={{ fontSize: 10, color: '#9CA3AF' }}>
        {week.trade_count} {t('profit_calendar.trades_suffix')}
      </div>
    </div>
  );
}

function EvaluationPanel({
  evaluation,
  t,
}: {
  evaluation: CalendarEvaluation;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const e = evaluation;
  const evalT = (key: string) => t(`profit_calendar.evaluation.${key}`);

  return (
    <div>
      <Card title={evalT('title')} style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Statistic title={evalT('sum_r_multiple')} value={e.sum_r_multiple} precision={2} suffix="R" valueStyle={{ color: getRColor(e.sum_r_multiple), fontWeight: 700 }} />
          </Col>
          <Col span={12}>
            <Statistic title={evalT('total_trades')} value={e.total_trades} />
          </Col>
          <Col span={12}>
            <Statistic title={evalT('trading_days')} value={e.trading_days} />
          </Col>
          <Col span={12}>
            <Statistic title={evalT('avg_profit_per_day')} value={e.avg_profit_per_day} precision={2} suffix="R" valueStyle={{ color: getRColor(e.avg_profit_per_day) }} />
          </Col>
          <Col span={12}>
            <Statistic title={evalT('winrate')} value={e.winrate} precision={1} suffix="%" valueStyle={{ color: e.winrate >= 50 ? '#10B981' : '#EF4444' }} />
          </Col>
          <Col span={12}>
            <Statistic title={evalT('profit_factor')} value={e.profit_factor} precision={2} valueStyle={{ color: e.profit_factor >= 1 ? '#10B981' : '#EF4444' }} />
          </Col>
          <Col span={12}>
            <Statistic title={evalT('biggest_winner')} value={e.biggest_winner} precision={2} suffix="R" valueStyle={{ color: '#10B981' }} />
          </Col>
          <Col span={12}>
            <Statistic title={evalT('biggest_loser')} value={e.biggest_loser} precision={2} suffix="R" valueStyle={{ color: '#EF4444' }} />
          </Col>
          <Col span={12}>
            <Statistic title={evalT('avg_winner')} value={e.avg_winner} precision={2} suffix="R" valueStyle={{ color: '#10B981' }} />
          </Col>
          <Col span={12}>
            <Statistic title={evalT('avg_loser')} value={e.avg_loser} precision={2} suffix="R" valueStyle={{ color: '#EF4444' }} />
          </Col>
          <Col span={12}>
            <Statistic title={evalT('avg_rrr')} value={e.avg_rrr} precision={2} />
          </Col>
        </Row>
      </Card>

      {e.current_streak && e.current_streak.length > 0 && (
        <Card title={evalT('current_streak')} size="small">
          <Space wrap>
            {e.current_streak.map((s, i) => (
              <Tag key={i} color={s === 'W' ? 'green' : 'red'} style={{ fontSize: 13, fontWeight: 600 }}>
                {s}
              </Tag>
            ))}
          </Space>
        </Card>
      )}
    </div>
  );
}
